import React, { useEffect, useState } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
} from "@mui/material";
import { Tablecell, normalizeTickerForComparison } from "../../../component/Methods";

const summarizeWeights = (signals) => {
  let positiveSum = 0;
  let negativeSum = 0;
  let netSum = 0;

  (signals || []).forEach((signal) => {
    const value = parseFloat(signal.value) || 0;
    if (Math.abs(value) < 0.0001) return;

    netSum += value;
    if (value > 0) positiveSum += value;
    else negativeSum += value;
  });

  return {
    gross: positiveSum - negativeSum,
    net: netSum,
  };
};

const SignalDifferenceSummary = ({ currentSignalsData, modifiedSignalsData }) => {
  const [CombinedArray, setCombinedArray] = useState([]);

  useEffect(() => {
    // Only show data when we have modified signals data
    if (
      modifiedSignalsData?.length > 0 &&
      currentSignalsData?.length > 0
    ) {
      // Use the utility function to normalize ticker for comparison
      // This treats '.' and '/' as equivalent (e.g., TSLA.A = TSLA/A)
      const normalizeTicker = normalizeTickerForComparison;

      // Helper function to normalize long_short value
      const normalizeLongShort = (value, existingLongShort) => {
        // Always derive from value to ensure accuracy (value is the source of truth)
        // If value is negative, it's short; if positive or zero, it's long
        const numValue = parseFloat(value) || 0;
        return numValue >= 0 ? "L" : "S";
      };

      // Create maps for easier lookup
      const currentMap = new Map();
      const modifiedMap = new Map();

      // Build current signals map
      currentSignalsData.forEach(signal => {
        const ticker = normalizeTicker(signal.name);
        if (!ticker) return;

        const value = parseFloat(signal.value) || 0;
        // Always derive long_short from value to ensure accuracy
        const longShort = normalizeLongShort(value, signal.long_short);

        currentMap.set(ticker, {
          ...signal,
          ticker,
          value: value,
          long_short: longShort,
        });
      });

      // Build modified signals map
      modifiedSignalsData.forEach(signal => {
        const ticker = normalizeTicker(signal.name);
        if (!ticker) return;

        const value = parseFloat(signal.value) || 0;
        // Always derive long_short from value to ensure accuracy
        const longShort = normalizeLongShort(value, signal.long_short);

        modifiedMap.set(ticker, {
          ...signal,
          ticker,
          value: value,
          long_short: longShort,
        });
      });

      // Find all unique tickers from both sets
      const allTickers = new Set([...currentMap.keys(), ...modifiedMap.keys()]);

      // Calculate metrics for new summary format
      let newTickersCount = 0;
      let removedTickersCount = 0;
      let consistentTickersCount = 0;
      let longWeightChange = 0;
      let shortWeightChange = 0;

      allTickers.forEach(ticker => {
        const currentSignal = currentMap.get(ticker);
        const modifiedSignal = modifiedMap.get(ticker);

        if (modifiedSignal && Math.abs(modifiedSignal.value || 0) < 0.0001) {
          removedTickersCount++;
        } else if (currentSignal && modifiedSignal) {
          const currentValue = parseFloat(currentSignal.value) || 0;
          const modifiedValue = parseFloat(modifiedSignal.value) || 0;
          const valueChanged = Math.abs(currentValue - modifiedValue) > 0.0001;
          const assetTypeChanged =
            (currentSignal.assetType || "").trim() !== (modifiedSignal.assetType || "").trim();
          const longShortChanged =
            (currentSignal.long_short || "").toUpperCase() !==
            (modifiedSignal.long_short || "").toUpperCase();

          if (valueChanged || assetTypeChanged || longShortChanged) {
            const weightDiff = modifiedValue - currentValue;

            if (currentValue >= 0 || modifiedValue >= 0) {
              longWeightChange += weightDiff;
            }
            if (currentValue < 0 || modifiedValue < 0) {
              shortWeightChange += weightDiff;
            }
          } else {
            consistentTickersCount++;
          }
        } else if (!currentSignal && modifiedSignal) {
          newTickersCount++;
          const modifiedValue = parseFloat(modifiedSignal.value) || 0;

          if (modifiedValue >= 0) {
            longWeightChange += modifiedValue;
          } else {
            shortWeightChange += modifiedValue;
          }
        } else if (currentSignal && !modifiedSignal) {
          removedTickersCount++;
          const currentValue = parseFloat(currentSignal.value) || 0;

          if (currentValue >= 0) {
            longWeightChange -= currentValue;
          } else {
            shortWeightChange -= currentValue;
          }
        }
      });

      const activeModifiedSignals = modifiedSignalsData.filter(
        (signal) => Math.abs(parseFloat(signal.value) || 0) >= 0.0001
      );
      const currentTotals = summarizeWeights(currentSignalsData);
      const modifiedTotals = summarizeWeights(activeModifiedSignals);
      const grossWeightDifference = modifiedTotals.gross - currentTotals.gross;
      const netWeightDifference = modifiedTotals.net - currentTotals.net;

      function roundit(number) {
        const n = Number(number);
        if (!Number.isFinite(n) || Math.abs(n) < 1e-9) return "0";
        return n.toFixed(2).replace(/\.?0+$/, "");
      }

      const transformedArray = [
        {
          isTickerLine: true,
          newTickersCount,
          removedTickersCount,
          consistentTickersCount,
        },
        { name: "Long Weight Difference", value: roundit(longWeightChange), showPercent: true },
        { name: "Short Weight Difference", value: roundit(shortWeightChange), showPercent: true },
        { name: "Gross Sum Difference", value: roundit(grossWeightDifference), showPercent: true },
        { name: "Net Sum Difference", value: roundit(netWeightDifference), showPercent: true },
      ];

      setCombinedArray(transformedArray);
    } else {
      setCombinedArray([]);
    }
  }, [currentSignalsData, modifiedSignalsData]);

  const theme = useTheme();

  // Don't render anything if there's no modified signals data
  if (!modifiedSignalsData?.length || !currentSignalsData?.length) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          border: `100px ${theme.palette.lightGrey.tableHeader}`,
          borderRadius: "4px",
          marginTop: "10px",
          width: "80%",
          marginRight: 1,
          minHeight: "120px",

        }}
      >
        <Table
          size="small"
          stickyHeader
          aria-label="a dense table"
          sx={{
            borderCollapse: "collapse",
            border: `1px solid ${theme.palette.divider}`,
            marginTop: "0px",
          }}
        >
          <TableBody>
            {CombinedArray?.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:hover": {
                    background: theme.palette.grey[200],
                    cursor: "pointer",
                  },

                  backgroundColor: "transparent",
                }}
              >
                {row.isTickerLine ? (
                  <Tablecell
                    colSpan={2}
                    align="left"
                    sx={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      paddingRight: "5px",
                      color: theme.palette.text.primary,
                      borderRight: "none",
                      borderLeft: "none",
                      borderTop: "none",
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        whiteSpace: "nowrap",
                        gap: 1,
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 900 }}>
                        New: {row.newTickersCount}
                      </Box>
                      <Box component="span" sx={{ fontWeight: 900 }}>
                        Removed: {row.removedTickersCount}
                      </Box>
                      <Box component="span" sx={{ fontWeight: 900 }}>
                        Consistent: {row.consistentTickersCount}
                      </Box>
                    </Box>
                  </Tablecell>
                ) : (
                  <>
                    <Tablecell
                      align="left"
                      sx={{
                        fontWeight: "900",
                        fontSize: "12px",
                        paddingLeft: "5px",
                        color: theme.palette.text.primary,
                        borderRight: "none",
                        borderLeft: "none",
                        borderTop: "none",
                        borderBottom:
                          row.name === "Short Weight Difference"
                            ? `1px solid ${theme.palette.divider}`
                            : "none",
                      }}
                    >
                      {row?.name}
                    </Tablecell>

                    <Tablecell
                      align="right"
                      sx={{
                        fontWeight: "500",
                        fontSize: "12px",
                        paddingLeft: "5px",
                        color: theme.palette.text.primary,
                        borderRight: "none",
                        borderLeft: "none",
                        borderTop: "none",
                        borderBottom:
                          row.name === "Short Weight Difference"
                            ? `1px solid ${theme.palette.divider}`
                            : "none",
                      }}
                    >
                      {row.showPercent ? `${row.value}%` : row.value}
                    </Tablecell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};
export default SignalDifferenceSummary;
